import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation, Outlet } from '@tanstack/react-router';
import { Box, Button, Text, LoadingOrbit, Page } from '@chatsdet/ui';
import type { ApplicationType } from '@chatsdet/types';
import { useQuery } from '@tanstack/react-query';
import { buildApplicationSubNavItems } from './applicationSubNavItems';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useSubNav } from '../../hooks/useSubNav';
import type { SubNavConfig } from '../../contexts/SubNavContext';
import { fetchOrgPropertyById } from '../../hooks/useOrgProperty';
import { PropertySettingsTab } from '../components/property-tabs/PropertySettingsTab';
import { PropertyAdvancedTab } from '../components/property-tabs/advanced/PropertyAdvancedTab';
import { PropertyTestUsersTab } from '../components/property-tabs/test-users/PropertyTestUsersTab';
import { PropertyCodeTab } from '../components/property-tabs/PropertyCodeTab';
import { PropertyEnvironmentsTab } from '../components/property-tabs/PropertyEnvironmentsTab';
import { PropertyInboxTab } from '../components/property-tabs/PropertyInboxTab';
import { PropertyContextTab } from '../../features/application-context';
import { CredentialDialogWithTest } from '../components/CredentialDialogWithTest';

const PROPERTY_TAB_SEGMENTS = [
  'code',
  'credentials',
  'environments',
  'context',
  'inbox',
  'settings',
  'advanced',
] as const;

function getActiveSegment(pathname: string, propertyId: string): string {
  const parts = pathname.split('/');
  const idx = parts.findIndex((part) => part === propertyId);
  const next = idx >= 0 ? parts[idx + 1] : undefined;
  if (next && PROPERTY_TAB_SEGMENTS.includes(next as (typeof PROPERTY_TAB_SEGMENTS)[number])) {
    return next;
  }
  return 'settings';
}

export function PropertyDetail() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  const navigate = useNavigate();
  const location = useLocation();

  const propertyQ = useQuery({
    queryKey: ['property', propertyId],
    queryFn: async () => {
      return fetchOrgPropertyById<{
        id: string;
        name: string;
        baseUrl: string;
        type: ApplicationType;
      }>(propertyId);
    },
  });

  const activeSegment = React.useMemo(
    () => getActiveSegment(location.pathname, propertyId),
    [location.pathname, propertyId]
  );

  const isMcp = propertyQ.data?.type === 'mcp';

  const subNavConfig = React.useMemo<SubNavConfig>(() => {
    const base = `/applications/${propertyId}`;
    return {
      title: 'Application',
      basePath: base,
      items: buildApplicationSubNavItems(base, isMcp),
      getActiveId: ({ pathname }) => getActiveSegment(pathname, propertyId),
    };
  }, [propertyId, isMcp]);

  useSubNav(subNavConfig);

  const segmentLabelMap: Record<string, string> = {
    code: 'Code',
    credentials: 'Test Users',
    environments: 'Environments',
    context: 'Skill',
    inbox: 'Messages',
    settings: 'General',
    advanced: 'Advanced',
  };

  useBreadcrumbs(
    propertyQ.data
      ? [
          { label: 'Applications', onPress: () => navigate({ to: '/applications' }) },
          {
            label: propertyQ.data.name,
            onPress: () => navigate({ to: `/applications/${propertyId}` }),
          },
          { label: segmentLabelMap[activeSegment] ?? 'General' },
        ]
      : []
  );

  if (propertyQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (!propertyQ.data) {
    return (
      <Box className="space-y-4">
        <Text>Application not found</Text>
        <Button onPress={() => navigate({ to: '/applications' })}>Back to Applications</Button>
      </Box>
    );
  }

  return (
    <Page>
      {/* Breadcrumbs already carry the application context. */}
      <Outlet />
    </Page>
  );
}

export function PropertySettingsView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  return <PropertySettingsTab propertyId={propertyId} />;
}

export function PropertyAdvancedView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  return <PropertyAdvancedTab propertyId={propertyId} />;
}

export function PropertyCodeView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  return <PropertyCodeTab propertyId={propertyId} />;
}

export function PropertyEnvironmentsView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  return (
    <Box className="space-y-6">
      <PropertyEnvironmentsTab propertyId={propertyId} />
    </Box>
  );
}

export function PropertyInboxView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  return <PropertyInboxTab propertyId={propertyId} />;
}

export function PropertyContextView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  return <PropertyContextTab propertyId={propertyId} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Test Users View (personas + credentials, with inline login test)
// ─────────────────────────────────────────────────────────────────────────────

export function PropertyCredentialsView() {
  const { propertyId } = useParams({ from: '/applications/$propertyId' });
  const qc = useQueryClient();

  const [isCredModalOpen, setIsCredModalOpen] = React.useState(false);
  const [editingCredentialId, setEditingCredentialId] = React.useState<string | null>(null);
  // Persona pre-binding when the dialog is opened from a persona group; the
  // persona select is locked for that flow.
  const [lockedTemplateId, setLockedTemplateId] = React.useState<string | null>(null);

  const handleAddCredential = React.useCallback((opts?: { templateId?: string }) => {
    setEditingCredentialId(null);
    setLockedTemplateId(opts?.templateId ?? null);
    setIsCredModalOpen(true);
  }, []);

  const handleEditCredential = React.useCallback((credId: string) => {
    setEditingCredentialId(credId);
    setLockedTemplateId(null);
    setIsCredModalOpen(true);
  }, []);

  const handleDialogClose = React.useCallback((open: boolean) => {
    setIsCredModalOpen(open);
    if (!open) {
      setEditingCredentialId(null);
      setLockedTemplateId(null);
    }
  }, []);

  const handleSuccess = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ['property-creds', propertyId] });
  }, [qc, propertyId]);

  return (
    <Box className="space-y-6">
      <PropertyTestUsersTab
        propertyId={propertyId}
        onAddCredential={handleAddCredential}
        onEditCredential={handleEditCredential}
      />

      {isCredModalOpen && (
        <CredentialDialogWithTest
          propertyId={propertyId}
          isOpen={isCredModalOpen}
          onOpenChange={handleDialogClose}
          initialCredentialId={editingCredentialId ?? undefined}
          initialTemplateId={lockedTemplateId}
          lockTemplate={!!lockedTemplateId}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
}
